import type { ComputedRef, InjectionKey, Ref } from 'vue';
import type { AccessLevel } from './useAccessLevel';

/**
 * Wave 1 — Property access gate (Agent C).
 *
 * Central composable that orchestrates the 3-tier access modals on
 * `pages/properties/[id].vue`.
 *
 *  • Wave 2 mounts ONE instance at the top of the page via
 *    `provideAccessGate()` and renders <AccessGateModalsHost/>.
 *  • Child components grab the same instance with `useAccessGate()`
 *    and call `gate(action, callback, ctx)` before performing any
 *    locked action.
 *
 * Decision matrix (level + action → modal):
 *   guest      + commission   → ModalGuestCommission
 *   guest      + document     → ModalGuestDocument
 *   guest      + *            → ModalGuestGeneric
 *   registered + availability → ModalVerifyAvailability
 *   registered + sales-offer  → ModalVerifyOfferPDF
 *   registered + dev-contact  → ModalVerifyDevContact
 *   registered + *            → ModalVerifyAvailability   (default verify)
 *   verified   + *            → no modal — runs callback
 */

export type GateAction = 'generic' | 'commission' | 'document' | 'availability' | 'sales-offer' | 'dev-contact';

export interface GateContext {
  /** Project / unit context the modal can render. */
  unitId?: string;
  unitLabel?: string;
  projectTitle?: string;
  /** Estimated commission for the teaser block on guest commission. */
  teaserAed?: string;
  /** Document context for document modals. */
  documentName?: string;
  documentMeta?: string;
  /** Developer-contact modal context. */
  developerName?: string;
  managerName?: string;
  managerRole?: string;
}

export interface GateOpenState {
  action: GateAction;
  ctx?: GateContext;
  resume?: () => void;
}

export interface AccessGate {
  level: ComputedRef<AccessLevel>;
  open: Readonly<Ref<GateOpenState | null>>;
  /**
   * Call before performing a gated action.
   *  - Verified user → runs `callback` synchronously and returns true.
   *  - Otherwise → opens the appropriate modal, queues the callback,
   *    and returns false.
   */
  gate: (action: GateAction, callback?: () => void, ctx?: GateContext) => boolean;
  close: () => void;
  /** Called by the OTP entry component on success — runs the queued callback. */
  resumeAfterVerify: () => Promise<void>;
}

const ACCESS_GATE_KEY: InjectionKey<AccessGate> = Symbol('AccessGate');

const createAccessGate = (): AccessGate => {
  const level = useAccessLevel();
  const open = ref<GateOpenState | null>(null);

  const gate = (action: GateAction, callback?: () => void, ctx?: GateContext): boolean => {
    if (level.value === 'verified') {
      callback?.();
      return true;
    }
    open.value = { action, ctx, resume: callback };
    return false;
  };

  const close = () => {
    open.value = null;
  };

  const resumeAfterVerify = async () => {
    const queued = open.value?.resume;
    open.value = null;
    // Allow Vue to flush the close before the queued action runs
    // (some callbacks navigate / open new popovers).
    await nextTick();
    queued?.();
  };

  return {
    level,
    open: readonly(open) as Readonly<Ref<GateOpenState | null>>,
    gate,
    close,
    resumeAfterVerify,
  };
};

/**
 * Provide a single AccessGate instance for the page subtree.
 * Call this once near the top of `pages/properties/[id].vue`.
 */
export const provideAccessGate = (): AccessGate => {
  const gate = createAccessGate();
  provide(ACCESS_GATE_KEY, gate);
  return gate;
};

/**
 * Inject the page-level AccessGate. Falls back to a freshly-created
 * instance if no provider is found — this is the normal case for
 * AccessTopNav when it's rendered standalone in layouts/default.vue
 * (the guest top nav on /developers, /explore, etc.), where there's no
 * provideAccessGate() ancestor. Passing the factory as inject()'s
 * default (3rd arg `true` = treat default as factory) suppresses Vue's
 * "injection not found" dev warning while keeping the same lazy create.
 */
export const useAccessGate = (): AccessGate => {
  return inject(ACCESS_GATE_KEY, createAccessGate, true);
};
